import { NumericValueOfNotAvailable, colorType, userImageType } from '../utils/constants';
import {
  getCoupon,
  getHasCustomUploadedLogos,
  getIfCurrentBrandNeedsToRecalculatePrice,
  getItemImprintAreaCode,
  getStudioItemType,
  getWhetherIsForGooglePLA,
  getZipCode,
} from '../main/selectors';
import {
  getDiscount,
  getFreeQuantity,
  getSellUnitPriceByQuantity,
  hasCouponApplied,
  hasFreeQuantityDiscount,
  hasItemPriceDiscount,
  isUnitPriceNotAvailable,
} from '../utils/PriceUtils';
import {
  getFinalBomOptionsSelection,
  getQuantity,
  getQuantityValidation,
  getShowSeparateQuantity,
  getWhetherCurrentContextIsBomOptionsPackage,
} from '../bom/selectors';

import { PricingStatus } from './constants';
import { SpecialChargeConditionNumber } from '../charges/constants';
import { TemplateType } from '../template/constants';
import { createSelector } from 'reselect';
import { getCanvasImageDataDictForAddCart } from '../canvas/selectors';
import { getFinalTemplateSelection } from '../template/selectors';
import { getImprintedTextDataDict } from '../text/selectors';
import { getIsLiteParentChildItem } from '../lite/selectors';
import { getSelectedImprintAreas } from '../imprintarea/selectors';
import { studioItemType as studioItemTypeEnum } from '../main/constants';

const getBasicChargeDetails = createSelector(
  getIfCurrentBrandNeedsToRecalculatePrice,
  getQuantityValidation,
  getQuantity,
  (state) => state.price,
  (state) => state.priceRecalculationResponse,
  (needToRecalculatePrice, validation, quantity, price, priceRecalculationResponse) => {
    let status, discount, total, unitPrice;
    let hasDiscount, isCalculating, unitPriceIsNotAvailable;
    if (needToRecalculatePrice) {
      ({ status, discount, totalPrice: total, unitPrice } = priceRecalculationResponse);
      hasDiscount = hasItemPriceDiscount(price) || (status === PricingStatus.Succeeded && discount > 0);
      isCalculating = status === PricingStatus.Loading;
      unitPriceIsNotAvailable = status !== PricingStatus.Succeeded;
    } else {
      discount = validation.isValid ? getDiscount(price, quantity) : NumericValueOfNotAvailable;
      hasDiscount = validation.isValid && hasItemPriceDiscount(price);
      unitPrice = validation.isValid ? getSellUnitPriceByQuantity(price, quantity) : NumericValueOfNotAvailable;
      unitPriceIsNotAvailable = !validation.isValid || isUnitPriceNotAvailable(price, quantity);
      total = unitPriceIsNotAvailable ? NumericValueOfNotAvailable : quantity * unitPrice;
      isCalculating = false;
      status = unitPriceIsNotAvailable ? PricingStatus.Failed : PricingStatus.Succeeded;
    }
    const freeQuantity = validation.isValid ? getFreeQuantity(price, quantity) : NumericValueOfNotAvailable;
    const essentialCharge = { hasDiscount, isCalculating, quantity, total, unitPrice };
    return { discount, essentialCharge, freeQuantity, status, unitPriceIsNotAvailable };
  }
);

const getSpecialChargesFromBom = createSelector(
  getQuantityValidation,
  getIsLiteParentChildItem,
  getFinalBomOptionsSelection,
  getShowSeparateQuantity,
  getQuantity,
  (state) => state.charges,
  (validation, isLiteParentChildItem, bomSelection, needSeparateQuantity, bomQuantity, charges) => {
    if (!validation.isValid || isLiteParentChildItem) return [];

    const chargeHelpers = [];
    const { generalOptions, pseudoParentChildQuestionOptionDictionaries } = bomSelection;
    const options = [
      ...generalOptions,
      ...pseudoParentChildQuestionOptionDictionaries.flatMap((dict) => Object.values(dict)).flat(),
    ];
    options.forEach((option) => {
      charges
        .filter(
          (charge) =>
            charge.chargeConditionNumber === SpecialChargeConditionNumber.BillOfMaterial &&
            charge.componentId === option.componentId
        )
        .forEach((charge) => {
          const quantity =
            charge.chargePerCode.toUpperCase() === 'L' ? 1 : needSeparateQuantity ? bomQuantity : option.quantity;
          chargeHelpers.push({ charge, quantity });
        });
    });

    return chargeHelpers
      .reduce((uniqueHelpers, currentHelper) => {
        const index = uniqueHelpers.findIndex((helper) => helper.charge.chargeId === currentHelper.charge.chargeId);
        if (index >= 0) {
          uniqueHelpers[index].quantity += currentHelper.quantity;
          return uniqueHelpers;
        } else {
          return [...uniqueHelpers, currentHelper];
        }
      }, [])
      .map((helper) => {
        const { charge, quantity } = helper;
        return {
          additionalChargeCount: 0,
          chargeAmount: charge.chargeAmount,
          chargeDescription: charge.chargeDescription,
          chargeName: charge.chargeName,
          hasDiscount: !charge.isNotAvailable && charge.chargeAmount !== charge.regularChargeAmount,
          isNotAvailable: charge.isNotAvailable,
          quantity: quantity,
          regularChargeAmount: charge.regularChargeAmount,
          regularTotal: charge.isNotAvailable ? NumericValueOfNotAvailable : quantity * charge.regularChargeAmount,
          total: charge.isNotAvailable ? NumericValueOfNotAvailable : quantity * charge.chargeAmount,
        };
      })
      .sort((chargeA, chargeB) => {
        const chargeNameA = chargeA.chargeName.toUpperCase();
        const chargeNameB = chargeB.chargeName.toUpperCase();

        if (chargeNameA < chargeNameB) {
          return -1;
        } else if (chargeNameA > chargeNameB) {
          return 1;
        } else {
          return 0;
        }
      });
  }
);

const hasBackground = (imprintAreaKey, templateDict) => {
  return (
    Object.keys(templateDict).includes(imprintAreaKey) &&
    !!templateDict[imprintAreaKey] &&
    templateDict[imprintAreaKey].templateType !== TemplateType.BLANK
  );
};

const hasCustomization = (imprintAreaKey, imageDict, textDict) => {
  return (
    (Object.keys(imageDict).includes(imprintAreaKey) && imageDict[imprintAreaKey].length > 0) ||
    (Object.keys(textDict).includes(imprintAreaKey) && textDict[imprintAreaKey].length > 0)
  );
};

const getAdditionalImprintChargeCount = createSelector(
  getSelectedImprintAreas,
  getCanvasImageDataDictForAddCart,
  getImprintedTextDataDict,
  (imprintAreas, imageDict, textDict) => {
    const count = imprintAreas.filter((imprintArea) => hasCustomization(imprintArea.key, imageDict, textDict)).length;
    return count > 0 ? count - 1 : 0;
  }
);

const getPmsColorChargeCount = createSelector(
  getCanvasImageDataDictForAddCart,
  getImprintedTextDataDict,
  (imageDict, textDict) => {
    const colorIds = [
      ...Object.values(imageDict)
        .flat()
        .filter((image) => image.colors)
        .map((image) => image.colors)
        .flat()
        .filter((color) => color.colorType === colorType.Pms)
        .map((color) => color.colorId),
    ];

    colorIds.push(
      ...Object.values(textDict)
        .flat()
        .filter((text) => text.color && text.color.colorType === colorType.Pms)
        .map((text) => text.color.colorId)
    );

    return colorIds.reduce(
      (uniqueColorIds, currentColorId) =>
        uniqueColorIds.findIndex((colorId) => colorId.toUpperCase() === currentColorId.toUpperCase()) >= 0
          ? uniqueColorIds
          : [...uniqueColorIds, currentColorId],
      []
    ).length;
  }
);

const getAdditionalColorChargeCount = createSelector(
  getCanvasImageDataDictForAddCart,
  getImprintedTextDataDict,
  getStudioItemType,
  (imageDict, textDict, studioItemType) => {
    if (studioItemType === studioItemTypeEnum.digital) {
      return 0;
    }

    const colorIds = [
      ...Object.values(imageDict)
        .flat()
        .filter((image) => image.colors)
        .map((image) => image.colors)
        .flat()
        .map((color) => color.colorId),
    ];

    colorIds.push(
      ...Object.values(textDict)
        .flat()
        .filter((text) => text.color)
        .map((text) => text.color.colorId)
    );

    let count = colorIds.reduce(
      (uniqueColorIds, currentColorId) =>
        uniqueColorIds.findIndex((colorId) => colorId.toUpperCase() === currentColorId.toUpperCase()) >= 0
          ? uniqueColorIds
          : [...uniqueColorIds, currentColorId],
      []
    ).length;

    count -= 1; // '1' stands for One Free
    return count >= 0 ? count : 0;
  }
);

const getWhetherShowSetupFeeForGooglePLAInBomOptionsPackageContext = createSelector(
  getItemImprintAreaCode,
  getWhetherIsForGooglePLA,
  getWhetherCurrentContextIsBomOptionsPackage,
  (imprintAreaCode, isForGooglePLA, isBomOptionsPackageContext) =>
    !!imprintAreaCode && isForGooglePLA && isBomOptionsPackageContext
);

const getInvolvedSpecialChargeConditionNumbersFromCustomization = createSelector(
  getQuantityValidation,
  getSelectedImprintAreas,
  getCanvasImageDataDictForAddCart,
  getImprintedTextDataDict,
  getFinalTemplateSelection,
  getAdditionalImprintChargeCount,
  getPmsColorChargeCount,
  getAdditionalColorChargeCount,
  getWhetherShowSetupFeeForGooglePLAInBomOptionsPackageContext,
  (
    validation,
    imprintAreas,
    imageDict,
    textDict,
    templateDict,
    additionalImprintChargeCount,
    pmsColorChargeCount,
    additionalColorChargeCount,
    showSetupFeeForGooglePLAInBomOptionsPackageContext
  ) => {
    if (!validation.isValid) return [];

    const images = Object.values(imageDict).flat();
    const texts = Object.values(textDict).flat();
    const templates = Object.values(templateDict).flat();

    const result = [SpecialChargeConditionNumber.MandatoryCharge];

    const hasLogo = images.some(
      (image) => image.type === userImageType.RecentArt || image.type === userImageType.UploadLogo
    );
    if (hasLogo) {
      result.push(SpecialChargeConditionNumber.LogoCharge);
    }

    const hasImprint = texts.length > 0;
    const hasVariousArts =
      texts.length === 0 &&
      images.some(
        (image) =>
          image.type === userImageType.SavedArt ||
          image.type === userImageType.SharedArt ||
          image.type === userImageType.StockArt ||
          image.type === userImageType.Sentiment ||
          image.type === userImageType.Verse
      );
    const hasTemplate = templates.some((template) => template && template.templateType !== TemplateType.BLANK);
    if (hasLogo || hasImprint || hasVariousArts || hasTemplate || showSetupFeeForGooglePLAInBomOptionsPackageContext) {
      result.push(SpecialChargeConditionNumber.Imprinted);
    }

    const hasClipImprint = imprintAreas.some(
      (imprintArea) =>
        imprintArea.location.trim().toUpperCase() === 'CLIP' && hasCustomization(imprintArea.key, imageDict, textDict)
    );
    if (hasClipImprint) {
      result.push(SpecialChargeConditionNumber.ClipImprint);
    }

    const hasCapImprint = imprintAreas.some(
      (imprintArea) =>
        imprintArea.location.trim().toUpperCase() === 'CAP' && hasCustomization(imprintArea.key, imageDict, textDict)
    );
    if (hasCapImprint) {
      result.push(SpecialChargeConditionNumber.CapImprint);
    }

    const hasLogoEnhancement = imprintAreas.some(
      (imprintArea) =>
        imprintArea.location.trim().toUpperCase() === 'ENH' && hasCustomization(imprintArea.key, imageDict, textDict)
    );
    if (hasLogoEnhancement) {
      result.push(SpecialChargeConditionNumber.LogoEnhancement);
    }

    const hasSlogan = images.some((image) => image.type === userImageType.Slogan);
    if (hasSlogan) {
      result.push(SpecialChargeConditionNumber.Slogan);
    }

    const hasAdditionalImprint = additionalImprintChargeCount >= 1;
    if (hasAdditionalImprint) {
      result.push(SpecialChargeConditionNumber.AdditionalImprintSetUpCharge);
      result.push(SpecialChargeConditionNumber.AdditionalImprintRunCharge);
    }

    const hasPmsColor = pmsColorChargeCount > 0;
    if (hasPmsColor) {
      result.push(SpecialChargeConditionNumber.PmsColorCharge);
    }

    const hasAdditionalColor = additionalColorChargeCount > 0;
    if (hasAdditionalColor) {
      result.push(SpecialChargeConditionNumber.AdditionalColorRunCharge);
      result.push(SpecialChargeConditionNumber.AdditionalColorSetUpCharge);
    }

    const hasInsideCover = imprintAreas.some(
      (imprintArea) =>
        imprintArea.isInsideCover &&
        (hasBackground(imprintArea.key, templateDict) || hasCustomization(imprintArea.key, imageDict, textDict))
    );
    if (hasInsideCover) {
      result.push(SpecialChargeConditionNumber.CustomizeYourInsideCover);
    }

    return result;
  }
);

const getSpecialChargesFromCustomization = createSelector(
  getInvolvedSpecialChargeConditionNumbersFromCustomization,
  getAdditionalColorChargeCount,
  getAdditionalImprintChargeCount,
  getPmsColorChargeCount,
  getQuantity,
  getHasCustomUploadedLogos,
  (state) => state.charges,
  (
    conditionNumbers,
    additionalColorChargeCount,
    additionalImprintChargeCount,
    pmsColorChargeCount,
    bomQuantity,
    hasCustomUploadedLogos,
    specialCharges
  ) => {
    const result = [];
    conditionNumbers.forEach((conditionNumber) => {
      let additionalChargeCount;
      switch (conditionNumber) {
        case SpecialChargeConditionNumber.AdditionalColorRunCharge:
        case SpecialChargeConditionNumber.AdditionalColorSetUpCharge:
          additionalChargeCount = additionalColorChargeCount;
          break;
        case SpecialChargeConditionNumber.AdditionalImprintRunCharge:
        case SpecialChargeConditionNumber.AdditionalImprintSetUpCharge:
          additionalChargeCount = additionalImprintChargeCount;
          break;
        case SpecialChargeConditionNumber.PmsColorCharge:
          additionalChargeCount = pmsColorChargeCount;
          break;
        default:
          additionalChargeCount = 0;
          break;
      }

      const charges =
        conditionNumber === SpecialChargeConditionNumber.MandatoryCharge
          ? specialCharges.filter((charge) => charge.chargeType.toUpperCase() === 'M')
          : specialCharges.filter((charge) => charge.chargeConditionNumber === conditionNumber);
      charges.forEach((charge) => {
        const chargeDescription = charge.chargeDescription;
        const chargeName = charge.chargeName;
        const isNotAvailable = charge.isNotAvailable;
        const chargeAmount = charge.chargeAmount;
        const regularChargeAmount = charge.regularChargeAmount;
        const hasDiscount = !isNotAvailable && chargeAmount !== regularChargeAmount;
        const quantity = charge.chargePerCode.toUpperCase() === 'L' ? 1 : bomQuantity;
        const regularTotal = isNotAvailable
          ? NumericValueOfNotAvailable
          : additionalChargeCount > 0
          ? additionalChargeCount * quantity * regularChargeAmount
          : quantity * regularChargeAmount;
        const total = isNotAvailable
          ? NumericValueOfNotAvailable
          : additionalChargeCount > 0
          ? additionalChargeCount * quantity * chargeAmount
          : quantity * chargeAmount;

        result.push({
          additionalChargeCount,
          chargeAmount,
          chargeDescription,
          chargeName,
          hasDiscount,
          isNotAvailable,
          quantity,
          regularChargeAmount,
          regularTotal,
          total,
        });
      });
    });
    return result;
  }
);

const getSpecialChargeDetails = createSelector(
  getSpecialChargesFromBom,
  getSpecialChargesFromCustomization,
  (chargesFromBom, chargesFromCustomization) => {
    const sumReducer = (total, currentValue) => total + currentValue;
    const charges = [...chargesFromCustomization, ...chargesFromBom];
    const specialCharges = charges.map((charge) => {
      const { additionalChargeCount, chargeAmount, chargeDescription, chargeName, hasDiscount, quantity, total } =
        charge;
      return {
        additionalChargeCount,
        chargeAmount,
        chargeDescription,
        chargeName,
        hasDiscount,
        isCalculating: false,
        quantity,
        total,
      };
    });
    const hasDiscount = charges.some((charge) => charge.hasDiscount);
    const hasUnavailableSpecialCharges = charges.some((charge) => charge.isNotAvailable);
    const regularTotal = hasUnavailableSpecialCharges
      ? NumericValueOfNotAvailable
      : charges.map((charge) => charge.regularTotal).reduce(sumReducer, 0);
    const total = hasUnavailableSpecialCharges
      ? NumericValueOfNotAvailable
      : charges.map((charge) => charge.total).reduce(sumReducer, 0);
    const discount = hasUnavailableSpecialCharges ? NumericValueOfNotAvailable : regularTotal - total;
    return { discount, hasDiscount, hasUnavailableSpecialCharges, specialCharges, total };
  }
);

export const getOrderSummaryHelper = createSelector(
  getQuantityValidation,
  getBasicChargeDetails,
  getSpecialChargeDetails,
  getCoupon,
  getZipCode,
  (state) => state.price,
  (state) => state.vendor,
  (validation, basicChargeDetails, specialChargeDetails, couponCode, zipCode, price, vendor) => {
    const { essentialCharge, freeQuantity, status, unitPriceIsNotAvailable } = basicChargeDetails;
    const { hasUnavailableSpecialCharges, specialCharges } = specialChargeDetails;
    const chargeIsNotAvailable = unitPriceIsNotAvailable || hasUnavailableSpecialCharges;
    const chargeDetails = {
      essentialCharge,
      freeQuantity,
      specialCharges,
    };
    const discount = chargeIsNotAvailable
      ? NumericValueOfNotAvailable
      : basicChargeDetails.discount + specialChargeDetails.discount;
    const totalWithoutShippingCharge = chargeIsNotAvailable
      ? NumericValueOfNotAvailable
      : essentialCharge.total + specialChargeDetails.total;

    const { isCalculating } = essentialCharge;
    const { coupon, hasNavInkDiscount, isClearance, isCouponApplied, isEveryDayLowPrice } = price;
    const isFreeQuantityCouponApplied = hasFreeQuantityDiscount(price);
    const hasNoCouponInput = !couponCode;
    const showCouponPrompt =
      hasNoCouponInput && !hasNavInkDiscount && !isClearance && !isEveryDayLowPrice && vendor.allowCoupons;
    const hasDiscount = essentialCharge.hasDiscount || hasCouponApplied(price) || specialChargeDetails.hasDiscount;
    const showDiscountText = hasDiscount && vendor.allowCoupons;
    const discountInfo = {
      chargeIsNotAvailable: !isCalculating && chargeIsNotAvailable,
      coupon,
      discount,
      isCalculating,
      isCouponApplied,
      isFreeQuantityCouponApplied,
      showCouponPrompt,
      showDiscountText,
    };

    const showShippingCharge = vendor.allowShipping && vendor.canCalculateShipping;
    const shippingInfo = {
      showShippingCharge,
      zipCode: zipCode || '',
    };

    const result = {
      chargeDetails,
      discountInfo,
      shippingInfo,
      status,
      totalWithoutShippingCharge,
      validation,
    };
    // console.log('Order Summary Helper:');
    // console.log(result);
    return result;
  }
);
